import { memo, useMemo } from 'react';
import { Button, Input, mergeClasses } from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import type { SettingsModalSection } from '@/hooks/useSettingsModal';
import { useSettingsModalStyles } from './SettingsModal.styles';
import type { SettingsSectionGroup } from './useSettingsSections';

interface SettingsNavProps {
  groups: SettingsSectionGroup[];
  selected: SettingsModalSection;
  onSelect: (section: SettingsModalSection) => void;
  query: string;
  onQueryChange: (query: string) => void;
}

/**
 * Left-rail navigation for the Settings modal. Filters the visible nav
 * items by a fuzzy substring match on label or group name when the user
 * types into the search box. Search filters the *nav* only — section
 * content remains unmodified.
 */
export const SettingsNav = memo<SettingsNavProps>(({ groups, selected, onSelect, query, onQueryChange }) => {
  const styles = useSettingsModalStyles();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((group) => {
        const groupMatches = group.label.toLowerCase().includes(q);
        const sections = groupMatches
          ? group.sections
          : group.sections.filter((s) => s.label.toLowerCase().includes(q));
        return sections.length > 0 ? { ...group, sections } : null;
      })
      .filter((g): g is SettingsSectionGroup => g !== null);
  }, [groups, query]);

  const noResults = query.trim().length > 0 && filtered.length === 0;

  return (
    <>
      <div className={styles.search}>
        <Input
          aria-label="Search settings"
          placeholder="Search settings…"
          value={query}
          type="search"
          contentBefore={<SearchRegular />}
          contentAfter={
            query ? (
              <Button
                appearance="transparent"
                size="small"
                icon={<DismissRegular />}
                onClick={() => onQueryChange('')}
                aria-label="Clear search"
                className={styles.searchClearButton}
              />
            ) : null
          }
          onChange={(_, data) => onQueryChange(data.value)}
          size="small"
          appearance="outline"
          className={styles.searchInput}
        />
      </div>
      <nav className={styles.nav} aria-label="Settings sections">
        {noResults && (
          <div className={styles.noResults} role="status">
            No settings match “{query.trim()}”.
          </div>
        )}
        {filtered.map((group) => (
          <div key={group.label}>
            <div className={styles.navGroup}>
              <span>{group.label}</span>
            </div>
            {group.sections.map((section) => {
              const isActive = section.id === selected;
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  className={mergeClasses(styles.navItem, isActive && styles.navItemActive)}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => onSelect(section.id)}
                >
                  <span className={mergeClasses(styles.navItemIcon, isActive && styles.navItemIconActive)}>
                    <Icon />
                  </span>
                  <span className={styles.navItemLabel}>{section.label}</span>
                  {section.badge && <span className={styles.navItemBadge}>{section.badge}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
    </>
  );
});

SettingsNav.displayName = 'SettingsNav';
